import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box, Checkbox, FormControlLabel, Grid, Paper, Stack, TablePagination, Typography } from '@mui/material';

import { useSnakeBar } from 'app/hooks';
import { ActionButton, SearchInput, useBreadcrumb } from 'app/components';
import PaperBackground from 'app/components/PaperBackground';
import { TranslationKeys } from 'app/translations';

import { ReportTypeSelector, ToDateField, EmployerList, SelectionIndicator } from './components';
import { ConfirmDialog } from './components/ConfirmDialog';
import { useDebounce, useCreateReportRequest, useReportEmployers, useSelection } from './hooks';
import {
  REPORT_TYPE_CONFIG,
  REPORT_TYPE_API_VALUE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_REPORT_TYPE,
  SEARCH_DEBOUNCE_MS,
} from './report-requests.constants';
import type { ReportRequestPayload, ReportType } from './report-requests.types';

export const ReportRequestsPage: React.FC = () => {
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  useBreadcrumb(TranslationKeys.menu_reports);

  const [selectedReportType, setSelectedReportType] = useState<ReportType>(DEFAULT_REPORT_TYPE);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const currentConfig = useMemo(() => REPORT_TYPE_CONFIG[selectedReportType], [selectedReportType]);

  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  const selection = useSelection(currentConfig.selectionMode);
  const createReportRequest = useCreateReportRequest();

  const { data, isLoading } = useReportEmployers({
    query: debouncedSearch || undefined,
    page: page + 1,
    perPage: rowsPerPage,
  });

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const handleReportTypeChange = useCallback(
    (type: ReportType) => {
      setSelectedReportType(type);
      selection.clear();
      setPage(0);
      setEndDate(null);
    },
    [selection],
  );

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleGenerate = useCallback(() => {
    setConfirmDialogOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    const payload: ReportRequestPayload = {
      reportType: REPORT_TYPE_API_VALUE[selectedReportType],
      selectAll: selection.state.selectAll,
      employersFilters: selection.state.selectAll
        ? []
        : Object.entries(selection.state.selections).map(([employerId, connectionIds]) => ({
            employerId,
            connections: connectionIds,
          })),
      ...(endDate && { endDateFilter: new Date(`${endDate}T00:00:00Z`).toISOString() }),
    };

    createReportRequest.mutate(
      { payload },
      {
        onSuccess: () => {
          setConfirmDialogOpen(false);
          showSuccessSnakeBar({
            method: 'generateReport',
            message: intl.formatMessage({ id: 'reporting.generateSuccess' }),
          });
        },
        onError: () => {
          setConfirmDialogOpen(false);
          showErrorSnakeBar({
            method: 'generateReport',
            message: intl.formatMessage({ id: 'reporting.generateError' }),
          });
        },
      },
    );
  }, [selectedReportType, selection.state, endDate, createReportRequest, showSuccessSnakeBar, showErrorSnakeBar, intl]);

  const handleCancelConfirm = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const isGenerateDisabled =
    (selection.selectedCount === 0 && !selection.state.selectAll) || createReportRequest.isLoading;

  const totalItems = data?.totalItems ?? 0;
  const employers = data?.items ?? [];

  return (
    <>
      {/* Top Bar — Report Configuration */}
      <Box sx={{ mt: 3 }}>
        <PaperBackground className="reports-top-bar">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md>
              <ReportTypeSelector value={selectedReportType} onChange={handleReportTypeChange} />
            </Grid>
            {currentConfig.hasEndDateFilter && (
              <Grid item xs={12} md="auto">
                <ToDateField value={endDate} onChange={setEndDate} />
              </Grid>
            )}
            <Grid item xs={12} md="auto">
              <ActionButton
                messageId="reporting.generateReport"
                onClick={handleGenerate}
                disabled={isGenerateDisabled}
              />
            </Grid>
          </Grid>
        </PaperBackground>
      </Box>

      {/* Toolbar — Search and Selection */}
      <Box sx={{ mt: 5, mb: 1 }}>
        <Grid container spacing={3} paddingLeft="20px" alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md="auto">
            <Typography variant="h5" sx={{ ml: 0.5 }}>
              {intl.formatMessage({
                id: currentConfig.showConnections
                  ? 'reporting.selectEmployersAndConnections'
                  : 'reporting.selectEmployers',
              })}
            </Typography>
            {currentConfig.allowSelectAll && (
              <FormControlLabel
                control={<Checkbox checked={selection.state.selectAll} onChange={selection.toggleSelectAll} />}
                label={intl.formatMessage({ id: 'reporting.selectAll' })}
              />
            )}
          </Grid>
          <Grid item xs={12} md="auto">
            <SearchInput onChange={handleSearchChange} disabled={isLoading} style={{ minWidth: 400 }} />
          </Grid>
          <Grid item xs={12} md="auto">
            <SelectionIndicator
              selectedCount={selection.selectedCount}
              selectAll={selection.state.selectAll}
              showConnections={currentConfig.showConnections}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Bottom Section — Data List */}
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <EmployerList
            employers={employers}
            isLoading={isLoading}
            selectionMode={currentConfig.selectionMode}
            showConnections={currentConfig.showConnections}
            connectionFilter={currentConfig.connectionFilter}
            isConnectionSelected={selection.isConnectionSelected}
            isEmployerSelected={selection.isEmployerSelected}
            onToggleConnection={selection.toggleConnection}
            onToggleEmployer={selection.toggleEmployer}
          />

          {totalItems > 0 && (
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </Stack>
      </Paper>

      <ConfirmDialog
        open={confirmDialogOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
        selectAll={selection.state.selectAll}
        isLoading={createReportRequest.isLoading}
        showConnections={currentConfig.showConnections}
        selections={selection.state.selections}
        employers={data?.items ?? []}
      />
    </>
  );
};
